'use client';

import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { ContextNavigation, ListPageSearch } from '@terra/ui-design-system';
import type { CountByStatusResponseDto } from '@terra/shared-contracts-agent-execution';
import type { AgentExecutionStatusType } from '@terra/shared-schemas-ai';
import { ListPageRefreshButton } from '../../common/ListPageRefreshButton';
import {
  AGENT_EXECUTIONS_STATUS_CHIP_ALL,
  AGENT_EXECUTIONS_STATUS_CHIPS,
  resolveAgentExecutionsStatusChip,
  resolveAgentExecutionsStatusChipCount,
  toAgentExecutionsStatusFilter,
  type AgentExecutionsStatusChip,
} from '../agent-executions-table/agent-executions-list.model';

type AgentExecutionsContextNavigationProps = {
  statusFilter: AgentExecutionStatusType[];
  onStatusFilterChange: (statusFilter: AgentExecutionStatusType[]) => void;
  statusCounts: Partial<CountByStatusResponseDto> | null;
  searchTerm: string;
  onSearchTermChange: (searchTerm: string) => void;
  onRefresh: () => void;
  isLoading: boolean;
};

export function AgentExecutionsContextNavigation({
  statusFilter,
  onStatusFilterChange,
  statusCounts,
  searchTerm,
  onSearchTermChange,
  onRefresh,
  isLoading,
}: AgentExecutionsContextNavigationProps) {
  const tCommon = useTranslations('designSystem.common');
  const tExecutions = useTranslations('designSystem.executions');
  const tStatus = useTranslations('agentExecutions.status');
  const items = useMemo(
    () =>
      AGENT_EXECUTIONS_STATUS_CHIPS.map((chip) => ({
        value: chip,
        label:
          chip === AGENT_EXECUTIONS_STATUS_CHIP_ALL
            ? tCommon('all')
            : tStatus(chip),
        count: resolveAgentExecutionsStatusChipCount({ chip, statusCounts }),
      })),
    [statusCounts, tCommon, tStatus],
  );

  return (
    <ContextNavigation<AgentExecutionsStatusChip>
      ariaLabel={tExecutions('filters.status')}
      items={items}
      value={resolveAgentExecutionsStatusChip(statusFilter)}
      onValueChange={(chip) =>
        onStatusFilterChange(toAgentExecutionsStatusFilter(chip))
      }
      actions={
        <>
          <ListPageSearch
            value={searchTerm}
            onValueChange={onSearchTermChange}
            placeholder={tExecutions('filters.searchByObjective')}
          />
          <ListPageRefreshButton onRefresh={onRefresh} isLoading={isLoading} />
        </>
      }
    />
  );
}
